import { useEffect } from 'react';
import type { GameClient } from './client.ts';

type Pausable = Pick<GameClient, 'canPause' | 'paused' | 'setPaused'>;

/**
 * A game that can be paused (solo, training, the tutorial) is paused when the page goes out of sight: another
 * app comes up, a call, the screen goes off. On a phone that is the only way it can happen without the clock
 * running on behind a screen nobody is looking at. It does not start again by itself: the player finds the
 * pause window and resumes when they are ready. An online game cannot be paused (GDD §11): nothing happens.
 */
export function useAutoPause(client: Pausable, active = true): void {
  useEffect(() => {
    if (!client.canPause || !active) return;
    const onVisibility = (): void => {
      if (document.hidden && !client.paused()) client.setPaused(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [client, active]);
}
